/**
 * Solution-analysis layouts — the named arrangements of one analysis, and the
 * rule for which one a student lands on.
 *
 * A layout owns everything below the tab strip on the Feedback tab: the score
 * card and the answer block above it are chrome and never vary, or we would be
 * comparing two things at once. Every layout is handed the same `Feedback`;
 * a layout may reorder, fold, promote or drop what it is given, never ask for
 * different content.
 *
 * Adding a layout is three lines: a row here, a file in
 * `shared-ui/solution-analysis/layouts/`, and an entry in that folder's
 * component map — which is typed against this list, so a missing component is
 * a compile error.
 *
 * See docs/FeatureDesigns/solution-analysis-layouts/solution-analysis-layouts-target.md.
 */

export interface SolutionAnalysisLayout {
    /** Stable, internal, never shown to a student. */
    readonly id: string;
    /** What the tab says. */
    readonly label: string;
    /** Superseded — kept so the id is never re-minted, not offered as a tab. */
    readonly retired?: true;
}

/**
 * EVERY layout that has ever shipped, in tab order.
 *
 * **Never remove a row, and never reuse an id for different content.** An id
 * appears in each student's stored preference and in every recorded event, and
 * it is the only thing tying a measurement to what was actually on screen.
 * Changing what an id renders pools two arrangements into one average with no
 * error and no signal — so a change to what appears above the fold, or to the
 * order it appears in, means a NEW id (`focused-v1` → `focused-v2`) and the old
 * row stays below marked `retired`. Colour, spacing and type size are not
 * changes in that sense; when it is genuinely unclear, bump.
 */
export const SOLUTION_ANALYSIS_LAYOUTS = [
    { id: 'classic-v1', label: 'Detailed' },
    { id: 'focused-v1', label: 'Focused' },
    { id: 'minimal-v1', label: 'Brief' },
] as const satisfies readonly SolutionAnalysisLayout[];

export type SolutionAnalysisLayoutId = typeof SOLUTION_ANALYSIS_LAYOUTS[number]['id'];

/** Offered as tabs today — retired rows stay above but are never shown. */
export const OFFERED_LAYOUTS: readonly SolutionAnalysisLayout[] =
    SOLUTION_ANALYSIS_LAYOUTS.filter(l => !('retired' in l));

/** What renders when the layout experiment is off — today's behaviour, so
 *  turning the gate off is a true revert rather than a third state. */
export const DEFAULT_LAYOUT_ID: SolutionAnalysisLayoutId = 'classic-v1';

/**
 * Where a student with no stored choice lands.
 *   `'balanced'` — spread across the offered layouts (while we are measuring)
 *   a layout id  — everyone lands there (how the answer ships, once we have one)
 */
export const SOLUTION_ANALYSIS_LAYOUT_INITIAL: 'balanced' | SolutionAnalysisLayoutId = 'balanced';

export function isKnownLayoutId(id: string | null | undefined): id is SolutionAnalysisLayoutId {
    return !!id && SOLUTION_ANALYSIS_LAYOUTS.some(l => l.id === id);
}

/** Offered, i.e. a stored preference pointing here is still honourable. */
export function isOfferedLayoutId(id: string | null | undefined): id is SolutionAnalysisLayoutId {
    return !!id && OFFERED_LAYOUTS.some(l => l.id === id);
}

/** FNV-1a 32-bit. Mirrors `functions/src/shared/experiments.ts`, which cannot be
 *  imported here (backend-only) — same function, same reasons: deterministic,
 *  dependency-free, and stable across restarts. */
function hash32(str: string): number {
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
    }
    return h >>> 0;
}

/**
 * Rendezvous ("highest random weight") pick: the offered layout maximising
 * `hash(userId:layoutId)`.
 *
 * NOT `hash(userId) % length`. Retiring a layout, or adding one, changes
 * `length` and re-assigns every student who has not chosen — mid-run, mixing
 * cohorts inside a single layout's rows. Rendezvous is just as deterministic
 * and just as balanced, and moves only the users who must move: a new layout
 * pulls roughly 1/n onto it and leaves the rest where they were.
 *
 * Ties break on id so the result is stable regardless of list order.
 */
export function pickBalancedLayout(userId: string): SolutionAnalysisLayoutId {
    let best = OFFERED_LAYOUTS[0];
    let bestWeight = -1;
    for (const layout of OFFERED_LAYOUTS) {
        const weight = hash32(`${userId}:${layout.id}`);
        if (weight > bestWeight || (weight === bestWeight && layout.id < best.id)) {
            best = layout;
            bestWeight = weight;
        }
    }
    return best.id as SolutionAnalysisLayoutId;
}

/**
 * Which layout to show, in priority order:
 *   1. the student's own choice, if they have made one and it is still offered
 *   2. `SOLUTION_ANALYSIS_LAYOUT_INITIAL` — a balanced assignment while we
 *      measure, or a pinned id once we have an answer
 *
 * A stored id that is unknown or retired counts as absent: keeping a student on
 * a layout nobody else can reach would make their rows uninterpretable.
 */
export function resolveLayoutId(userId: string, storedChoice?: string | null): SolutionAnalysisLayoutId {
    if (isOfferedLayoutId(storedChoice)) return storedChoice;
    if (SOLUTION_ANALYSIS_LAYOUT_INITIAL !== 'balanced') return SOLUTION_ANALYSIS_LAYOUT_INITIAL;
    return pickBalancedLayout(userId);
}

// ── Measurement ──────────────────────────────────────────────────────

/**
 * What a student did with the tabs. Every kind carries `layoutId` — the id is
 * the only link between a row and what was on screen, which is why ids are
 * immutable.
 */
export type SolutionAnalysisLayoutEventKind = 'shown' | 'switched' | 'answered';

export interface SolutionAnalysisLayoutEvent {
    kind: SolutionAnalysisLayoutEventKind;
    /** The layout in front of the student when this happened. On `switched`,
     *  the one they moved TO. */
    layoutId: string;
    /** Its index in the tab strip. Order is fixed and the INITIAL SELECTION is
     *  what varies, so left-most salience should be a second-order effect —
     *  logged so that can be checked rather than assumed. */
    position: number;
    /** Which analysis this was about; lets a switch be tied to one attempt. */
    submissionId?: string;
    /** `shown`: true when the layout came from the student's own stored choice
     *  rather than the balanced assignment. Separates preference from exposure. */
    wasStored?: boolean;
    /** `switched`: the layout they moved away FROM, and how long they had it
     *  open. Dwell is recorded but is NOT a preference metric — a longer layout
     *  takes longer to read. */
    fromLayoutId?: string;
    msOnPrevious?: number;
    /** `answered`: the sampled prompt's structured reply. */
    answer?: 'short' | 'right' | 'much';
    /** `answered`: which layouts this student had opened by then. A "just right"
     *  from someone who has seen one layout is not the same datum as one from
     *  someone who has compared three. */
    seenLayoutIds?: string[];
    /** `answered`: the prompt was forced for testing rather than sampled. Kept
     *  so the reader can exclude it — a few minutes of testing would otherwise
     *  outweigh a day of real responses in a small sample. */
    forced?: boolean;
}

/**
 * Whether to offer the feedback prompt for this (student, analysis).
 *
 * Deterministic on the pair, not `Math.random()` per render: a prompt that
 * flickers in and out as the student navigates back and forth reads as a bug,
 * and would be answered or dismissed by accident.
 *
 * `rate` is a percentage. Start it high — at a small cohort a 10% sample of an
 * already-low completion rate rounds to nothing.
 */
export function shouldOfferLayoutFeedback(userId: string, submissionId: string, rate: number): boolean {
    if (rate <= 0) return false;
    if (rate >= 100) return true;
    return hash32(`layout-feedback:${userId}:${submissionId}`) % 100 < rate;
}

/**
 * Sampling rate for the feedback prompt, as a percentage of ANALYSES — not of
 * students. Combined with the client's one-ask-per-session cap, a student is
 * asked at most once per session and, on a twelve-question set, about a third
 * of sessions.
 *
 * Start high: a link-then-panel flow trades response rate for a lighter panel,
 * and at a small cohort a low sample of an already-low completion rate rounds
 * to nothing.
 */
export const LAYOUT_FEEDBACK_SAMPLE_RATE = 30;
